import { Component, EventEmitter, Input, Output, ViewChild, forwardRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInput, MatInputModule } from '@angular/material/input';
import { FilterChip } from '../../models/filter-chip';
import { BehaviorSubject, debounceTime, distinct, filter, Subject, switchMap, tap } from 'rxjs';
import { UsersService } from '../../services/user.service';
import { User } from '../../models/user';
import { MatDialog } from '@angular/material/dialog';
import { AdGroupsChooserDialogComponent } from '../dialogs/ad-groups-chooser-dialog/ad-groups-chooser-dialog.component';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { UIPreference, UserManagementPreferences } from '../../models/ui-preferences';
import { ADGroup } from '../../models/ad-group';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { ADADGroupService } from '../../services/adGroup.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

@Component({
  selector: 'app-bulk-user-chooser',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    MatAutocompleteModule,
    MatInputModule,
    CdkDropList, CdkDrag,
    MatSnackBarModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './bulk-user-chooser.component.html',
  styleUrls: ['./bulk-user-chooser.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BulkUserChooserComponent),
      multi: true
    }
  ]
})
export class BulkUserChooserComponent implements ControlValueAccessor {
  @Input() label: string = 'Filter';
  @Input() hint: string = '';
  @Input() chips: string[] = []; // List of possible chip types
  @Output() usersChange = new EventEmitter<User[]>();
  @ViewChild(MatInput) inputElement!: MatInput;
  @ViewChild(MatAutocompleteTrigger) autocomplete!: MatAutocompleteTrigger;
  readonly debouncer: Subject<User[]> = new Subject<User[]>();
  _options = new Subject<User[]>();
  options = this._options.asObservable();
  _dialog = inject(MatDialog);
  _adGroupService = inject(ADADGroupService);
  _snackbar = inject(MatSnackBar);

  _filter = new Subject<string>();
  filter = this._filter.asObservable();
  _loadingSubj = new BehaviorSubject<boolean>(false);
  loading = this._loadingSubj.pipe(debounceTime(200));

  filterControl = new FormControl('');
  @Input() selectedUsers: User[] = [];
  activeChip: FilterChip | null = null; // Tracks chip being edited

  onChange: (value: User[]) => void = () => { };
  onTouched: () => void = () => { };
  recentADGroups: string[] = [];
  uiPreference?: UIPreference;
  currentUser?: User;

  constructor(private userService: UsersService) {
    this.debouncer.pipe(debounceTime(100)).subscribe(v => this.usersChange.emit(this.selectedUsers))
    this.filterControl.valueChanges.pipe(
      filter(c => c != null && typeof c === "string"),
      debounceTime(100),
      tap(v => console.log(v)),
      switchMap(c => this.userService.filterUsers([{ chip: null, value: c?.trim() }], 5, 0, "fullname", "asc"))
    ).subscribe(resp => this._options.next(resp.users));

    this.userService.getCurrentUser().subscribe(user => {
      this.currentUser = user;
      this.uiPreference = user.userUIPreference?.data ? JSON.parse(user.userUIPreference.data) : new UIPreference();
      //const roles = user.userRoles.map(ur => ur.role.roleName.toLowerCase());
      //this.canAdd = roles.includes("admin") || (roles.includes("fullaccess") && !!user.groupMemberships.length);

      this.recentADGroups = [...this.uiPreference?.users?.recentADGroups ?? []]; // shallow copy
    });
  }

  ngOnChanges() {
    this.debouncer.next(this.selectedUsers);// Initial emit, if there are any...
  }

  addFromADGroup($event: any) {
    this.autocomplete.closePanel();
    this.autocomplete.autocompleteDisabled = true;
    this._dialog.open(AdGroupsChooserDialogComponent).afterClosed().subscribe((choice: [ADGroup[], User[]]) => {
      const [adGroups, users] = choice;
      console.log(adGroups, users);
      this.recentADGroups = [...new Set([...this.recentADGroups, ...adGroups.map(g => g.group)])];
      this.autocomplete.autocompleteDisabled = false;
      this.selectedUsers.push(...users);
      this.updateValue();
      this.saveRecentGroupsPref();
    });
  }

  removeRecentGroup(sam: string) {
    this.recentADGroups = this.recentADGroups.filter(g => g != sam);
    this.saveRecentGroupsPref();
  }

  private saveRecentGroupsPref() {
    if (!this.uiPreference || !this.currentUser) return;

    this.uiPreference!.users ??= new UserManagementPreferences();
    this.uiPreference!.users!.recentADGroups = this.recentADGroups;
    this.userService.savePreference(this.uiPreference!, this.currentUser!.id).subscribe(uipref => {
      console.info("Saved recent AD groups preference.");
    });
  }

  /**
   * Handles Enter key when inputting free text filters.
   */
  addFreeTextFilter(event: MatChipInputEvent): void {
    console.info(this.addFreeTextFilter.name);
    // if (!rawValue) {
    //   this.autocomplete.closePanel(); // Assume the user is trying to clear the autocomplete with a second "enter" press.
    //   return; // Ignore empty values
    // }
    const user = this.autocomplete.activeOption?.value;
    if (user && !this.selectedUsers.find(su => su.hawkId == user.hawkId)) {
      this.selectedUsers.push(user);
    }

    // Clear input field after adding a filter
    //this.filterControl.setValue('');
    event.chipInput!.clear();
    this.updateValue();
  }

  /**
   * Removes a selected filter chip.
   */
  removeUser(user: User): void {
    this.selectedUsers = this.selectedUsers.filter(f => f !== user);
    this.updateValue();
  }

  async addFromGroup(groupSam: string) {
    this._loadingSubj.next(true);
    const members = await this._adGroupService.fetchADGroupMembers(groupSam);
    if (members.length > 0) {
      this.selectedUsers.push(...members.filter(m => !this.selectedUsers.find(su => su.hawkId == m.hawkId)));
      this.updateValue();
    } else {
      this._snackbar.open("Warning: AD group was empty or had no TKS members.", undefined, { duration: 3000, verticalPosition: 'top' });
    }
    this._loadingSubj.next(false);
  }

  dropChip(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.recentADGroups, event.previousIndex, event.currentIndex);
  }

  handleEnterKey(event: Event) {
    this.filterControl.setValue('');
    this.inputElement.value = '';
    // console.debug(this.handleEnterKey.name);
    // if (this.autocomplete.activeOption) {
    //   // Manually emit optionSelected behavior
    //   this.selectUser({ option: { value: this.autocomplete.activeOption.value } } as MatAutocompleteSelectedEvent);
    //   this.filterControl.setValue('');
    //   event.preventDefault(); // Prevents chip input from handling Enter
    // }
  }

  /**
   * Handles chip selection from autocomplete.
   * - Moves the input into chip-edit mode.
   */
  selectUser(event: MatAutocompleteSelectedEvent): void {
    console.debug(this.selectUser.name);
    // console.info(this.selectUser.name);
    const selectedUser = event.option.value;
    if (!selectedUser || this.selectedUsers.find(su => su.hawkId == selectedUser.hawkId))
      return;
    this.filterControl.setValue('');
    this.inputElement.value = '';
    this.selectedUsers.push(selectedUser);
    this.updateValue();
  }

  private updateValue() {
    this.onChange(this.selectedUsers);
    this.debouncer.next(this.selectedUsers);
  }
  // **ControlValueAccessor Methods**
  writeValue(value: User[]): void {
    this.selectedUsers = value || [];
  }

  registerOnChange(fn: (value: User[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.filterControl.disable();
    } else {
      this.filterControl.enable();
    }
  }
}
